import React, { createContext, useState, useEffect, useContext, useRef, useCallback } from 'react';
import { isTokenValid, isTokenExpiringSoon, getTimeUntilRefresh, shouldPublishAuthToken } from '../lib/tokenUtils';
import {
  getAccessToken,
  setAccessToken as setSessionToken,
  clearSession,
  refreshAccessToken as sessionRefresh,
  logoutOnServer,
  registerAuthSessionListeners,
  STORAGE_KEY,
} from '../lib/authSession';
import { setupAxiosAuth } from '../lib/setupAxiosAuth';
import { invalidateUserMe } from '../lib/userMeCache';

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [accessToken, setAccessTokenState] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);
  const tokenRef = useRef(null);
  const startRefreshTimerRef = useRef(() => {});
  const REFRESH_BUFFER_MINUTES = 2;
  const endingSessionRef = useRef(false);

  const clearRefreshTimer = () => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  };

  const publishFromSession = useCallback((token) => {
    const next = token || null;
    const prev = tokenRef.current;
    tokenRef.current = next;
    if (shouldPublishAuthToken(prev, next)) {
      setAccessTokenState(next);
      setIsAuthenticated(!!next);
    }
  }, []);

  const applyToken = useCallback((token) => {
    if (token) {
      setSessionToken(token);
    } else {
      invalidateUserMe();
      clearSession();
    }
  }, []);

  const endSession = useCallback(() => {
    if (endingSessionRef.current) {
      return;
    }
    endingSessionRef.current = true;
    clearRefreshTimer();
    invalidateUserMe();
    clearSession();
    endingSessionRef.current = false;
  }, []);

  const refreshAccessToken = useCallback(async () => {
    return sessionRefresh();
  }, []);

  const startRefreshTimer = useCallback((token) => {
    if (!token) {
      return;
    }

    clearRefreshTimer();

    if (isTokenExpiringSoon(token, REFRESH_BUFFER_MINUTES)) {
      refreshAccessToken().catch((error) => {
        console.error('Immediate token refresh failed:', error);
        endSession();
      });
      return;
    }

    const timeUntilRefresh = getTimeUntilRefresh(token, REFRESH_BUFFER_MINUTES);
    if (timeUntilRefresh === null || timeUntilRefresh <= 0) {
      return;
    }

    refreshTimerRef.current = setTimeout(() => {
      refreshAccessToken().catch((error) => {
        console.error('Scheduled token refresh failed:', error);
        endSession();
      });
    }, timeUntilRefresh);
  }, [refreshAccessToken, endSession]);

  startRefreshTimerRef.current = startRefreshTimer;

  // Install axios interceptors + sync listeners once
  useEffect(() => {
    setupAxiosAuth();
    registerAuthSessionListeners({
      onTokenUpdated: (token) => {
        publishFromSession(token);
        if (token) {
          startRefreshTimerRef.current(token);
        } else {
          clearRefreshTimer();
        }
      },
      onSessionEnded: () => {
        endSession();
      },
    });
  }, [endSession, publishFromSession]);

  // Bootstrap session: valid stored token, or refresh via cookie
  useEffect(() => {
    let cancelled = false;

    const bootstrap = async () => {
      setupAxiosAuth();
      const storedToken = localStorage.getItem(STORAGE_KEY);

      try {
        if (storedToken && isTokenValid(storedToken)) {
          if (!cancelled) {
            applyToken(storedToken);
          }
        } else {
          // Access missing/expired — try refresh cookie before logging out
          if (storedToken) {
            localStorage.removeItem(STORAGE_KEY);
          }
          try {
            await sessionRefresh();
          } catch {
            if (!cancelled) {
              clearSession();
            }
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [applyToken]);

  // Proactive refresh timer (login / logout). Silent refresh reschedules in onTokenUpdated.
  useEffect(() => {
    if (accessToken) {
      startRefreshTimer(accessToken);
    } else {
      clearRefreshTimer();
    }
    return () => {
      clearRefreshTimer();
    };
  }, [accessToken, startRefreshTimer]);

  // Tab wake-up: refresh if expired or near expiry
  useEffect(() => {
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') {
        return;
      }
      const token = getAccessToken();
      // Skip on login/signup when there is no session to recover
      if (!token && !localStorage.getItem(STORAGE_KEY)) {
        return;
      }
      if (!token || !isTokenValid(token) || isTokenExpiringSoon(token, REFRESH_BUFFER_MINUTES)) {
        refreshAccessToken().catch((error) => {
          console.error('Visibility token refresh failed:', error);
          endSession();
        });
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [refreshAccessToken, endSession]);

  const login = (token) => {
    invalidateUserMe();
    applyToken(token);
  };

  const logout = async () => {
    clearRefreshTimer();
    await logoutOnServer();
    endSession();
  };

  return (
    <AuthContext.Provider
      value={{
        accessToken,
        isAuthenticated,
        login,
        logout,
        loading,
        refreshAccessToken,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
